import type { ApiVersion } from "./api";

export const queryKeys = {
  auth: {
    /** Keyed by the stored user so a new sign-in never reuses the last user's answer. */
    me: (userKey: string) => ["auth", "me", userKey] as const,
    /** The full own-profile payload (name, picture, sign-in method); `me` holds only the superadmin flag. */
    profile: (userKey: string) => ["auth", "profile", userKey] as const,
  },
  platformAdmin: {
    secrets: (tenantId: string | null) => ["platformAdmin", "secrets", tenantId] as const,
    prompts: (filters: object) => ["platformAdmin", "prompts", filters] as const,
    prompt: (name: string) => ["platformAdmin", "prompt", name] as const,
    promptTest: (workflowId: string) => ["platformAdmin", "promptTest", workflowId] as const,
  },
  adminSignups: {
    /** Prefix for every sign-up list page — invalidate after a decision. */
    all: ["adminSignups"] as const,
    list: (tab: string, offset: number) =>
      ["adminSignups", "list", tab, offset] as const,
  },
  tenantUi: {
    settings: ["tenantUi", "settings"] as const,
  },
  modelling: {
    status: ["modelling", "status"] as const,
    changes: (runId: string) => ["modelling", "changes", runId] as const,
  },
  agents: {
    all: ["agents"] as const,
    list: (workspaceId: string) => ["agents", workspaceId, "list"] as const,
  },
  home: {
    sections: (workspaceId: string) =>
      ["home", workspaceId, "sections"] as const,
    artifacts: (workspaceId: string) =>
      ["home", workspaceId, "artifacts"] as const,
    briefing: (workspaceId: string, date: string) =>
      ["home", workspaceId, "briefing", date] as const,
    follows: (workspaceId: string) => ["home", workspaceId, "follows"] as const,
    attention: (workspaceId: string) => ["home", workspaceId, "attention"] as const,
    /** Prefix of every feed query of a workspace, for patching or invalidating them all. */
    feedAll: (workspaceId: string) => ["home", workspaceId, "feed"] as const,
    briefingReels: (workspaceId: string) => ["home", workspaceId, "briefingReels"] as const,
    briefingReelVoice: (voiceUrl: string) => ["home", "briefingReelVoice", voiceUrl] as const,
    briefingAudio: (workspaceId: string, date: string) =>
      ["home", workspaceId, "briefingAudio", date] as const,
    feed: (
      workspaceId: string,
      lens: string,
      kinds: readonly string[],
      sort: string,
    ) => ["home", workspaceId, "feed", lens, [...kinds], sort] as const,
  },
  discover: {
    /** Prefix for every Discover list of one workspace. */
    all: (workspaceId: string) => ["discover", workspaceId] as const,
    list: (workspaceId: string, paramsKey: string) =>
      ["discover", workspaceId, "list", paramsKey] as const,
    insight: (workspaceId: string, insightId: string) =>
      ["discover", workspaceId, "insight", insightId] as const,
    watch: (workspaceId: string, insightId: string) => ["discover", workspaceId, "watch", insightId] as const,
    thread: (workspaceId: string, insightId: string) => ["discover", workspaceId, "thread", insightId] as const,
    settings: (workspaceId: string, insightId: string) => ["discover", workspaceId, "settings", insightId] as const,
    job: (workspaceId: string, jobId: string) => ["discover", workspaceId, "job", jobId] as const,
    sources: (workspaceId: string) => ["discover", workspaceId, "sources"] as const,
  },
  users: {
    detail: (userId: string) => ["users", userId] as const,
  },
  /** A deck theme's font file, registered with the document once per url per session (pd7). */
  themeFonts: {
    file: (url: string) => ["themeFonts", "file", url] as const,
  },
  mentions: {
    all: ["mentions"] as const,
    /**
     * One entry per (story, query) pair. The query string is part of the key
     * so an already-fetched prefix is served from cache when the user backspaces.
     */
    suggestions: (storyId: string, query: string) =>
      [...queryKeys.mentions.all, storyId, "suggestions", query] as const,
  },
  kg: {
    /** Loose prefix matching every graph() key for a workspace — use for invalidation. */
    graphRoot: (workspaceId: string) => ["kg", "graph", workspaceId] as const,
    graph: (workspaceId: string, paramsKey?: string) =>
      ["kg", "graph", workspaceId, paramsKey ?? ""] as const,
    overview: (nodeId: string) => ["kg", "overview", nodeId] as const,
    tableOverview: (schemaId: string) =>
      ["kg", "table", schemaId, "overview"] as const,
    tables: (dataSourceId: string) =>
      ["kg", "datasources", dataSourceId, "tables"] as const,
    reports: (dataSourceId: string) =>
      ["kg", "datasources", dataSourceId, "reports"] as const,
    reportSalesforceData: (workspaceId: string, reportId: string) =>
      ["kg", "workspaces", workspaceId, "reports", reportId, "salesforce-data"] as const,
    usedIn: (dataSourceId: string) =>
      ["kg", "datasources", dataSourceId, "used-in"] as const,
    schemaRelationships: (schemaId: string) =>
      ["kg", "schemas", schemaId, "relationships"] as const,
    schemaMetrics: (schemaId: string) =>
      ["kg", "schemas", schemaId, "metrics"] as const,
    schemaUsedIn: (schemaId: string) =>
      ["kg", "schemas", schemaId, "used-in"] as const,
    ontologyOverview: (ontologyId: string) =>
      ["kg", "ontologies", ontologyId, "overview"] as const,
    ontologyLinks: (ontologyId: string) =>
      ["kg", "ontologies", ontologyId, "links"] as const,
    ontologyMetrics: (ontologyId: string) =>
      ["kg", "ontologies", ontologyId, "metrics"] as const,
    ontologyRules: (ontologyId: string) =>
      ["kg", "ontologies", ontologyId, "rules"] as const,
    ontologyReports: (ontologyId: string) =>
      ["kg", "ontologies", ontologyId, "reports"] as const,
    ontologyUsedIn: (ontologyId: string) =>
      ["kg", "ontologies", ontologyId, "used-in"] as const,
    dataModelOverview: (dataModelId: string) =>
      ["kg", "data-models", dataModelId, "overview"] as const,
    dataModelTables: (dataModelId: string) =>
      ["kg", "data-models", dataModelId, "tables"] as const,
    dataModelMetrics: (dataModelId: string) =>
      ["kg", "data-models", dataModelId, "metrics"] as const,
    dataModelRelationships: (dataModelId: string) =>
      ["kg", "data-models", dataModelId, "relationships"] as const,
    dataSourcesList: (paramsKey?: string) =>
      ["kg", "data-sources", "list", paramsKey ?? ""] as const,
    tablesList: (paramsKey?: string) =>
      ["kg", "tables", "list", paramsKey ?? ""] as const,
    tablePreview: (tableId: string, paramsKey?: string) =>
      ["kg", "tables", tableId, "preview", paramsKey ?? ""] as const,
    reportsList: (paramsKey?: string) =>
      ["kg", "reports", "list", paramsKey ?? ""] as const,
    reportPreview: (reportId: string) =>
      ["kg", "reports", reportId, "preview"] as const,
    metricsList: (paramsKey?: string) =>
      ["kg", "metrics", "list", paramsKey ?? ""] as const,
    businessRulesList: (paramsKey?: string) =>
      ["kg", "business-rules", "list", paramsKey ?? ""] as const,
    ontologiesList: () => ["kg", "ontologies", "list"] as const,
    ontologiesPage: (paramsKey?: string) =>
      ["kg", "ontologies", "page", paramsKey ?? ""] as const,
    ontologyDetail: (ontologyId: string) =>
      ["kg", "ontologies", ontologyId, "detail"] as const,
    ontologyLinksFull: (ontologyId: string) =>
      ["kg", "ontologies", ontologyId, "links-full"] as const,
    relationVerbs: () => ["kg", "relation-verbs"] as const,
    ontologyAssociations: (ontologyId: string, section: string) =>
      ["kg", "ontologies", ontologyId, section] as const,
    ontologyAvailableAssociations: (ontologyId: string, section: string) =>
      ["kg", "ontologies", ontologyId, section, "available"] as const,
    metricOverview: (metricId: string) =>
      ["kg", "metrics", metricId, "overview"] as const,
    metricFormula: (metricId: string) =>
      ["kg", "metrics", metricId, "formula"] as const,
    metricUsedIn: (metricId: string) =>
      ["kg", "metrics", metricId, "used-in"] as const,
    reportOverview: (reportId: string) =>
      ["kg", "reports", reportId, "overview"] as const,
    reportTrace: (reportId: string) =>
      ["kg", "reports", reportId, "trace"] as const,
    reportUsedIn: (reportId: string) =>
      ["kg", "reports", reportId, "used-in"] as const,
    reportActivity: (reportId: string) =>
      ["kg", "reports", reportId, "activity"] as const,
    dataStoryOverview: (dataStoryId: string) =>
      ["kg", "data-stories", dataStoryId, "overview"] as const,
    dataStoryTrace: (dataStoryId: string) =>
      ["kg", "data-stories", dataStoryId, "trace"] as const,
    dataStoryActivity: (dataStoryId: string) =>
      ["kg", "data-stories", dataStoryId, "activity"] as const,
    artifactOverview: (artifactId: string) =>
      ["kg", "artifacts", artifactId, "overview"] as const,
    artifactActivity: (artifactId: string) =>
      ["kg", "artifacts", artifactId, "activity"] as const,
    dataStoriesList: (q?: string) =>
      ["kg", "data-stories", "list", q ?? ""] as const,
  },
  dataModels: {
    list: (workspaceId: string, paramsKey?: string) =>
      ["dataModels", workspaceId, "list", paramsKey ?? ""] as const,
    catalog: (paramsKey?: string) =>
      ["dataModels", "catalog", paramsKey ?? ""] as const,
    builder: (dataModelId: string) =>
      ["dataModels", "builder", dataModelId] as const,
    table: (tableId: string) => ["dataModels", "table", tableId] as const,
    /**
     * A model release's field catalog for one search term. The release is
     * shared by every chart built on it, so this is keyed by release, not by
     * chart; the empty term is the unsearched catalog bound chips read labels from.
     */
    fieldCatalog: (modelReleaseId: string, search: string) =>
      ["dataModels", "fieldCatalog", modelReleaseId, search] as const,
    /** One field's hover-card stats inside a model release. */
    fieldStats: (modelReleaseId: string, fieldId: string) =>
      ["dataModels", "fieldStats", modelReleaseId, fieldId] as const,
    /** The fields a story's filter pickers offer; `paramsKey` tells the global and each chart's list apart. */
    storyFields: (storyId: string, paramsKey: string) =>
      ["dataModels", "storyFields", storyId, paramsKey] as const,
    /** One field's value pages for one search; `paramsKey` carries `role`, `story_id` and `canvas_item_id`. */
    fieldValues: (modelReleaseId: string, fieldId: string, paramsKey: string) =>
      ["dataModels", "fieldValues", modelReleaseId, fieldId, paramsKey] as const,
  },
  /** Post-login tenant selection and the workspaces reachable inside a tenant. */
  tenantContext: {
    all: ["tenantContext"] as const,
    memberships: () => [...queryKeys.tenantContext.all, "memberships"] as const,
    workspaces: (tenantId: string) =>
      [...queryKeys.tenantContext.all, tenantId, "workspaces"] as const,
  },
  /** Invite-preview lookups, keyed by the raw token from the emailed link. */
  invites: {
    preview: (token: string) => ["invites", token, "preview"] as const,
  },
  /** The organization's SSO config (secret never returned). */
  tenantSso: {
    detail: (tenantId: string) => ["tenantSso", tenantId] as const,
    serviceProvider: (tenantId: string) => ["tenantSso", tenantId, "serviceProvider"] as const,
  },
  /** Active tenant member search, for the story-invite picker. */
  tenantUsers: {
    all: ["tenantUsers"] as const,
    search: (tenantId: string, query: string) =>
      [...queryKeys.tenantUsers.all, tenantId, "search", query] as const,
  },
  /** Column sensitivity review: queue, decisions, dataset fields. */
  columnSensitivity: {
    all: ["columnSensitivity"] as const,
    queue: (filters: object) => [...queryKeys.columnSensitivity.all, "queue", filters] as const,
    decision: (id: string) => [...queryKeys.columnSensitivity.all, "decision", id] as const,
    tenantColumns: (query: object) => [...queryKeys.columnSensitivity.all, "tenantColumns", query] as const,
    datasetFields: (datasetId: string, page: number) =>
      [...queryKeys.columnSensitivity.all, "fields", datasetId, page] as const,
    connectorDatasets: (connectorId: string) =>
      [...queryKeys.columnSensitivity.all, "connectorDatasets", connectorId] as const,
  },
  /** Full invite lifecycle (pending/accepted/expired/revoked) for a story. */
  storyShareLinks: {
    all: ["storyShareLinks"] as const,
    detail: (storyId: string) => ["storyShareLinks", storyId] as const,
  },
  storyInvitees: {
    all: ["storyInvitees"] as const,
    list: (storyId: string) =>
      [...queryKeys.storyInvitees.all, storyId, "list"] as const,
  },
  /**
   * Threads (root + replies) for one story. `all` is per-story (not a single
   * global root like `storyInvitees.all`) so a resolve/edit/delete mutation on
   * one story invalidates only that story's cached threads, not every story's.
   */
  storyComments: {
    all: (storyId: string) => ["storyComments", storyId] as const,
    /** Every server-side list filter is part of the cache identity. */
    list: (
      storyId: string,
      params: {
        canvas_item_id?: string;
        scope?: "global";
        mine?: boolean;
        status?: "open" | "resolved";
        mentions?: "me";
        q?: string;
        limit?: number;
        offset?: number;
      } = {},
    ) =>
      [
        ...queryKeys.storyComments.all(storyId),
        "list",
        params.canvas_item_id ?? "",
        params.scope ?? "",
        params.mine ?? false,
        params.status ?? "",
        params.mentions ?? "",
        params.q ?? "",
        params.limit ?? 50,
        params.offset ?? 0,
      ] as const,
    /** GET /comments/counts — nested under `all` so every list mutation's existing invalidation covers it too. */
    counts: (storyId: string) =>
      [...queryKeys.storyComments.all(storyId), "counts"] as const,
  },
  presentationThemes: {
    all: ["presentation-themes"] as const,
    list: () => [...queryKeys.presentationThemes.all, "list"] as const,
  },
  /** A Story's data app: the latest version, and its block draws per version + control values. */
  dataApps: {
    all: ["dataApps"] as const,
    latest: (storyId: string) => ["dataApps", storyId, "latest"] as const,
    /** `controlsKey` from a stable serialisation of the sent control values. */
    draw: (storyId: string, dataAppId: string, version: number, controlsKey: string) =>
      ["dataApps", storyId, dataAppId, version, "draw", controlsKey] as const,
    /** The SQL behind one block for one version and set of picks; the executed text is its own entry. */
    blockSql: (storyId: string, dataAppId: string, version: number, blockId: string, controlsKey: string, executed: boolean) =>
      ["dataApps", storyId, dataAppId, version, "block-sql", blockId, controlsKey, executed ? "executed" : "readable"] as const,
    versions: (storyId: string) => ["dataApps", storyId, "versions"] as const,
    version: (storyId: string, dataAppId: string) => ["dataApps", storyId, "version", dataAppId] as const,
    /** The progress log of one version; the events seen so far are merged into it as it is polled. */
    events: (storyId: string, dataAppId: string) => ["dataApps", storyId, "events", dataAppId] as const,
    /** An improve the API accepted whose upgrade `latest` may not report yet (a client-side note; never fetched). */
    improving: (storyId: string) => ["dataApps", storyId, "improving"] as const,
    /** What the 404 on `latest` said about the viewer (`can_manage`); a client-side note, never fetched. */
    noApp: (storyId: string) => ["dataApps", storyId, "no-app"] as const,
    /** What was made from one version (its outputs, newest first). */
    outputs: (storyId: string, dataAppId: string) => ["dataApps", storyId, "outputs", dataAppId] as const,
    /** The progress log of one output; the events seen so far are merged into it as it is polled. */
    outputEvents: (storyId: string, outputId: string) => ["dataApps", storyId, "output-events", outputId] as const,
  },
  /**
   * Personalized distribution. Everything about one distribution sits under `item`, so a
   * write invalidates `list` plus `item` and every read of that distribution refetches.
   */
  distributions: {
    all: ["distributions"] as const,
    story: (storyId: string) => ["distributions", storyId] as const,
    list: (storyId: string) => ["distributions", storyId, "list"] as const,
    /** The archived ones; under `list`, so refetching the list refetches this too. */
    archivedList: (storyId: string) => ["distributions", storyId, "list", "archived"] as const,
    /** Listed per audience: whether a field can split depends on who receives the copies. */
    splitFields: (storyId: string, audience: string) => ["distributions", storyId, "split-fields", audience] as const,
    /** `splitKey` from `splitFieldKey(split)`. */
    /** Per audience: a tile whose values are all above its clearance is left out as masked. */
    splitCheck: (storyId: string, splitKey: string, audience: string) =>
      ["distributions", storyId, "split-check", splitKey, audience] as const,
    /** The POST that starts a value-stats request; values are redacted at the audience's clearance. */
    valueStatsRequest: (storyId: string, splitKey: string, audience: string) =>
      ["distributions", storyId, "value-stats", splitKey, audience] as const,
    /** The polled result of one value-stats request. */
    valueStatsResult: (storyId: string, requestId: string) =>
      ["distributions", storyId, "value-stats-result", requestId] as const,
    /** `paramsKey` is the serialised request body. */
    schedulePreview: (storyId: string, paramsKey: string) =>
      ["distributions", storyId, "schedule-preview", paramsKey] as const,
    // Round 6 · WP5 (FE-recipients): recipients from the data model.
    modelFields: (storyId: string) => ["distributions", storyId, "model-fields"] as const,
    /** `bodyKey` is the serialised model-rows request body. */
    modelRowsRequest: (storyId: string, bodyKey: string) =>
      ["distributions", storyId, "model-rows", bodyKey] as const,
    modelRowsResult: (storyId: string, requestId: string) =>
      ["distributions", storyId, "model-rows-result", requestId] as const,
    item: (storyId: string, distributionId: string) =>
      ["distributions", storyId, "item", distributionId] as const,
    detail: (storyId: string, distributionId: string) =>
      ["distributions", storyId, "item", distributionId, "detail"] as const,
    recipients: (storyId: string, distributionId: string, offset: number, limit: number) =>
      ["distributions", storyId, "item", distributionId, "recipients", offset, limit] as const,
    /** `filtersKey`: round 6's trigger/status filters, e.g. "trigger=test|status=failed" ("" for all). */
    runs: (storyId: string, distributionId: string, offset: number, limit: number, filtersKey = "") =>
      ["distributions", storyId, "item", distributionId, "runs", offset, limit, filtersKey] as const,
    run: (storyId: string, distributionId: string, runId: string) =>
      ["distributions", storyId, "item", distributionId, "run", runId] as const,
    slice: (storyId: string, distributionId: string, runId: string, sliceId: string) =>
      ["distributions", storyId, "item", distributionId, "run", runId, "slice", sliceId] as const,
    /** Round 6 · message variables: under `item`, so a recipients or content write refetches them. */
    tokens: (storyId: string, distributionId: string) =>
      ["distributions", storyId, "item", distributionId, "tokens"] as const,
    /** Tenant-wide, not per story. */
    templates: () => ["distributions", "templates"] as const,
    /** GET /api/v2/distribution-themes */
    themes: () => ["distributions", "themes"] as const,
    // ─── Round 6 · brand kits (WP7) ───
    /** GET /api/v2/distribution-brand-kits — tenant-wide, not per story. */
    brandKits: () => ["distributions", "brand-kits"] as const,
    // ─── end round 6 · brand kits ───
    // ─── Round 7 · uploaded PowerPoint templates (pd7 FE-master) ───
    /** Tenant-wide, not per story. `detail` is polled while action-engine reads the upload. */
    masters: {
      all: () => ["distributions", "masters"] as const,
      /** GET /api/v2/distribution-templates/masters/{master_id} */
      detail: (masterId: string) => ["distributions", "masters", masterId] as const,
    },
    // ─── end round 7 · uploaded PowerPoint templates ───
    // ─── Round 7 · live copy preview (FE-preview) ───
    // Not under `item`: a distribution write invalidates `item`, and refetching a preview-run
    // entry would start another run. Kept for the session (one run per copy's data).
    /** Every preview run the copy preview started for a distribution. */
    copyPreviewRuns: (storyId: string, distributionId: string) =>
      ["distributions", storyId, "copy-preview", distributionId, "run"] as const,
    /** One draw of live copy tiles for a copy's data: `dataKey` from `liveTilesKey`. */
    liveTiles: (storyId: string, distributionId: string, dataKey: string) =>
      ["distributions", storyId, "live-tiles", distributionId, dataKey] as const,
    /** The run-free preview render (POST …/preview-render, then GET) for one copy's data: `dataKey` from `copyPreviewDataKey`. */
    copyPreviewRender: (storyId: string, distributionId: string, dataKey: string) =>
      ["distributions", storyId, "copy-preview", distributionId, "run", dataKey] as const,
    // ─── end round 7 · live copy preview ───
  },
  workspaces: {
    all: ["workspaces"] as const,
    userSearch: (query: string) =>
      [...queryKeys.workspaces.all, "user-search", query] as const,
    dataSourceList: (query: string) =>
      [...queryKeys.workspaces.all, "data-source-list", query] as const,
    detail: (workspaceId: string) =>
      [...queryKeys.workspaces.all, workspaceId, "detail"] as const,
    members: (workspaceId: string, paramsKey: string) =>
      [...queryKeys.workspaces.all, workspaceId, "members", paramsKey] as const,
    linkedDataSources: (workspaceId: string, q?: string) =>
      [...queryKeys.workspaces.all, workspaceId, "linked-data-sources", q ?? ""] as const,
    dataStories: (workspaceId: string, q?: string) =>
      [...queryKeys.workspaces.all, workspaceId, "data-stories", q ?? ""] as const,
    /** Prefix shared by every `dataStories` search variant for a workspace —
     * pass to `invalidateQueries` to refresh the list regardless of the
     * cached search term. */
    dataStoriesRoot: (workspaceId: string) =>
      [...queryKeys.workspaces.all, workspaceId, "data-stories"] as const,
  },
  pipeline: {
    run: (runId: string) => ["pipeline", "run", runId] as const,
    latest: (workspaceId: string) => ["pipeline", "latest", workspaceId] as const,
  },
  connectors: {
    all: ["connectors"] as const,
    registry: () => [...queryKeys.connectors.all, "registry"] as const,
    catalogSchema: (catalogKey: string) =>
      [...queryKeys.connectors.all, "catalog-schema", catalogKey] as const,
    detail: (connectionId: string) =>
      [...queryKeys.connectors.all, "detail", connectionId] as const,
    instances: (catalogKey: string) =>
      [...queryKeys.connectors.all, "instances", catalogKey] as const,
    datasets: (connectionId: string) =>
      [...queryKeys.connectors.all, "datasets", connectionId] as const,
    logs: (connectionId: string) =>
      [...queryKeys.connectors.all, "logs", connectionId] as const,
    embedToken: (
      connectionId: string,
      connectorAssetId?: string,
      version: ApiVersion = "v1",
    ) =>
      [
        ...queryKeys.connectors.all,
        "embedToken",
        connectionId,
        connectorAssetId ?? "",
        version,
      ] as const,
  },
  connectorsV2: {
    all: ["connectorsV2"] as const,
    /** Outside `all`: a connector write never changes the types. */
    types: () => ["connectorTypesV2"] as const,
    /** Inside `all`: a new connector changes `connector_count`. */
    catalog: () => [...queryKeys.connectorsV2.all, "catalog"] as const,
    dailyRuns: (days: number) => [...queryKeys.connectorsV2.all, "dailyRuns", days] as const,
    runs: (id: string, offset: number, limit: number) => [...queryKeys.connectorsV2.all, "runs", id, offset, limit] as const,
    usedIn: (id: string) => [...queryKeys.connectorsV2.all, "usedIn", id] as const,
    list: (offset: number) => [...queryKeys.connectorsV2.all, "list", offset] as const,
    detail: (id: string) => [...queryKeys.connectorsV2.all, "detail", id] as const,
    datasets: (id: string, q: string, selectedOnly: boolean, offset: number) =>
      [...queryKeys.connectorsV2.all, "datasets", id, q, selectedOnly, offset] as const,
    uploads: (id: string) => [...queryKeys.connectorsV2.all, "uploads", id] as const,
    syncs: (id: string, offset: number) =>
      [...queryKeys.connectorsV2.all, "syncs", id, offset] as const,
    driftEvents: (id: string) => [...queryKeys.connectorsV2.all, "driftEvents", id] as const,
  },
  liveView: {
    fields: (assetId: string, version: string) =>
      ["liveView", "fields", assetId, version] as const,
    data: (assetId: string, version: string, filters: unknown) =>
      ["liveView", "data", assetId, version, filters] as const,
  },
  storyBuilder: {
    config: (storyId: string) =>
      ["story-builder", "config", storyId] as const,
  },
  notifications: {
    all: ["notifications"] as const,
    slackConnection: () =>
      [...queryKeys.notifications.all, "slack", "connection"] as const,
    slackChannels: () =>
      [...queryKeys.notifications.all, "slack", "channels"] as const,
    scheduledRuleForStory: (storyId: string) =>
      [...queryKeys.notifications.all, "rules", "scheduled", storyId] as const,
    deliveryRules: (storyId: string) =>
      [...queryKeys.notifications.all, "rules", storyId] as const,
    deliveryRule: (ruleId: string) =>
      [...queryKeys.notifications.all, "rules", "detail", ruleId] as const,
    alertMetrics: (workspaceId: string, storyId: string) =>
      [...queryKeys.notifications.all, "alert-metrics", workspaceId, storyId] as const,
  },
  stories: {
    list: (workspaceId: string) =>
      ["stories", workspaceId, "list"] as const,
    /**
     * The same list fetched page-by-page. Deliberately a DIFFERENT key from
     * `list` above: this one caches React Query's `InfiniteData` envelope
     * (`{ pages, pageParams }`), and sharing a key with the single-shot `list`
     * would hand one consumer the other's shape. `pageSize` is part of the key
     * because changing it changes every page boundary. `filters` (backend
     * search / excluded story) is part of the key for the same reason: each
     * search term is its own paged result set.
     */
    listPaged: (
      workspaceId: string,
      pageSize: number,
      filters: { q: string; excludeStoryId: string } = { q: "", excludeStoryId: "" },
    ) => ["stories", workspaceId, "list", "paged", pageSize, filters] as const,
    /** Prefix shared by every story-scoped key below (canvas, chat,
     * presentation, ...) — pass to `invalidateQueries` to blow away all of a
     * story's cached data at once, e.g. after a manual refresh. The story row
     * itself is `storyMeta.detail`, outside this prefix. */
    storyRoot: (workspaceId: string, storyId: string) =>
      ["stories", workspaceId, storyId] as const,
    plan: (workspaceId: string, storyId: string, planId: string) =>
      ["stories", workspaceId, storyId, "plans", planId] as const,
    canvasManifest: (workspaceId: string, storyId: string) =>
      ["stories", workspaceId, storyId, "canvas", "manifest"] as const,
    dataSources: (workspaceId: string, storyId: string) =>
      ["stories", workspaceId, storyId, "data-sources"] as const,
    members: (workspaceId: string, storyId: string) =>
      ["stories", workspaceId, storyId, "members"] as const,
    lineage: {
      request: (storyId: string) => ["stories", "lineage", storyId, "request"] as const,
      item: (storyId: string, itemId: string, requestId: string) =>
        ["stories", "lineage", storyId, "item", itemId, requestId] as const,
      graphRequest: (storyId: string, elementsKey: string) =>
        ["stories", "lineage", storyId, "graph-request", elementsKey] as const,
      graph: (requestId: string) => ["stories", "lineage", "graph", requestId] as const,
      artifacts: (storyId: string) => ["stories", "lineage", storyId, "artifacts"] as const,
    },
    canvasWidget: (workspaceId: string, storyId: string, widgetId: string) =>
      ["stories", workspaceId, storyId, "canvas", "widget", widgetId] as const,
    canvasWidgetSalesforceData: (workspaceId: string, storyId: string, widgetId: string) =>
      ["stories", workspaceId, storyId, "canvas", "widget", widgetId, "salesforce-data"] as const,
    canvasWidgetDataModelFields: (
      workspaceId: string,
      storyId: string,
      widgetId: string,
      search: string,
    ) =>
      ["stories", workspaceId, storyId, "canvas", "widget", widgetId, "data-model-fields", search] as const,
    canvasWidgetHubspotData: (workspaceId: string, storyId: string, widgetId: string) =>
      ["stories", workspaceId, storyId, "canvas", "widget", widgetId, "hubspot-data"] as const,
    canvasDataset: (workspaceId: string, storyId: string, canvasItemId: string) =>
      ["stories", workspaceId, storyId, "canvas", "dataset", canvasItemId] as const,
    canvasSearch: (
      workspaceId: string,
      q: string,
      types: string,
      limit: number,
      version: ApiVersion = "v1",
    ) =>
      ["stories", workspaceId, "canvas", "search", q, types, limit, version] as const,
    chatHistory: (workspaceId: string, storyId: string, page?: number) =>
      ["stories", workspaceId, storyId, "chat", "history", page ?? 1] as const,
    presentation: (workspaceId: string, storyId: string) =>
      ["stories", workspaceId, storyId, "presentation"] as const,
    /** Background pre-render of the Presentation tab's deck, once per story open. */
    presentationPrerender: (workspaceId: string, storyId: string) =>
      ["stories", workspaceId, storyId, "presentation-prerender"] as const,
    /** Export modal render (file + page PNGs) per modal session, format and theme. */
    presentationPreview: (
      workspaceId: string,
      storyId: string,
      format: string,
      themeKey: string,
      /** Export modal: its open count. Presentation tab: "deck-<n>". Delivery:
       *  "delivery-<n>" — so none of the three ever share a render. */
      sessionId: number | string,
      /** Export modal/Presentation tab omit this (all pages); delivery passes 1. */
      previewPageLimit?: number,
    ) =>
      [
        "stories",
        workspaceId,
        storyId,
        "presentation-preview",
        format,
        themeKey,
        sessionId,
        ...(previewPageLimit !== undefined ? [previewPageLimit] : []),
      ] as const,
    presentationArtifactLatest: (workspaceId: string, storyId: string) =>
      [
        "stories",
        workspaceId,
        storyId,
        "presentation-artifact",
        "latest",
      ] as const,
  },
  /**
   * The story row (status, title, description, publish status, auto-refresh
   * flag). Outside `stories.*` on purpose: `stories.storyRoot` is the prefix of
   * every per-story key (canvas, chat, presentation), so cancelling a refetch
   * of this one row would otherwise strand a widget's first load in pending.
   * The cost: a `storyRoot` invalidate does not reach it — invalidate both
   * (see Header's refresh-now handler).
   */
  storyMeta: {
    detail: (workspaceId: string, storyId: string) =>
      ["storyMeta", workspaceId, storyId] as const,
  },
  /**
   * v2 canvas items, keyed by story alone like their flat routes. Kept apart
   * from `stories.*` for the same prefix reason as `storyMeta`.
   */
  /** A tenant's custom colour palettes — tenant-scoped, shared by every chart. */
  colorPalettes: {
    list: (tenantId: string) => ["colorPalettes", tenantId, "list"] as const,
  },
  /** Chart Studio v2 server state. Previews are mutations (a POST), so only reads are keyed. */
  chartStudioV2: {
    catalog: () => ["chartStudioV2", "catalog"] as const,
    /** `rel` is a release id, or `current`. */
    modelFields: (rel: string) => ["chartStudioV2", "modelFields", rel] as const,
    /** Mutation keys, per story, so the page can tell it is drawing or saving. */
    preview: (storyId: string) => ["chartStudioV2", "preview", storyId] as const,
    draw: (storyId: string) => ["chartStudioV2", "draw", storyId] as const,
    create: (storyId: string) => ["chartStudioV2", "create", storyId] as const,
  },
  storyItems: {
    /** Prefix of every key below: a regeneration invalidates the lot. */
    story: (storyId: string) => ["storyItems", storyId] as const,
    /** `?detail=summary`: the per-item `editable` gate. */
    summaries: (storyId: string) => ["storyItems", storyId, "summaries"] as const,
    /** The Story's layout, from its new-path read. */
    layout: (storyId: string) => ["storyItems", storyId, "layout"] as const,
    detail: (storyId: string, itemId: string) =>
      ["storyItems", storyId, "detail", itemId] as const,
    /**
     * A mutation key, not a query: one item's PATCHes. Also their `scope`, so
     * they reach the server one at a time, in order.
     */
    patch: (storyId: string, itemId: string) =>
      ["storyItems", storyId, "patch", itemId] as const,
  },
  storyFilters: {
    /** The whole list for a story; a mutation response replaces it via setQueryData. */
    list: (storyId: string) => ["storyFilters", storyId, "list"] as const,
    /** A mutation key, not a query: every write on the story. Also their `scope`, so writes reach the server one at a time. */
    write: (storyId: string) => ["storyFilters", storyId, "write"] as const,
  },
  files: {
    list: (workspaceId: string) =>
      ["files", "list", workspaceId] as const,
    preview: (workspaceId: string, fileId: string) =>
      ["files", "preview", workspaceId, fileId] as const,
  },
  assets: {
    /** Bytes for one object_key. Keys are immutable, so this never goes stale. */
    content: (workspaceId: string, objectKey: string) =>
      ["assets", "content", workspaceId, objectKey] as const,
  },
  csv: {
    filePreview: (workspaceId: string, fileId: string, limit: number) =>
      ["csv", "file-preview", workspaceId, fileId, limit] as const,
  },
  agentRuns: {
    result: (storyId: string, runId: string) =>
      ["agentRuns", storyId, runId] as const,
  },
  dataStudio: {
    all: ["dataStudio"] as const,
    importJob: (jobId: string) =>
      [...queryKeys.dataStudio.all, "importJob", jobId] as const,
    /**
     * Asset-scoped keys carry the connection id as well as the asset id: the
     * asset search is cross-connector, so an asset id alone is not a unique
     * cache identity.
     */
    assetSearch: (query: string) =>
      [...queryKeys.dataStudio.all, "assetSearch", query] as const,
    tableProfile: (connectionId: string, assetId: string) =>
      [...queryKeys.dataStudio.all, "tableProfile", connectionId, assetId] as const,
    recommendations: (connectionId: string, assetId: string, profileHash: string | null) =>
      [...queryKeys.dataStudio.all, "recommendations", connectionId, assetId, profileHash] as const,
    tableProfileRows: (connectionId: string, assetId: string) =>
      [
        ...queryKeys.dataStudio.all,
        "tableProfileRows",
        connectionId,
        assetId,
      ] as const,
    tableProfileColumns: (connectionId: string, assetId: string) =>
      [
        ...queryKeys.dataStudio.all,
        "tableProfileColumns",
        connectionId,
        assetId,
      ] as const,
    columnDetails: (connectionId: string, assetId: string, columnId: string) =>
      [
        ...queryKeys.dataStudio.all,
        "columnDetails",
        connectionId,
        assetId,
        columnId,
      ] as const,
  },
} as const;
